import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker, getDefaultClassNames, type DayButton } from 'react-day-picker';
import { cn } from '@/utils/cn';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const ref = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  const isRangeEndpoint = modifiers.range_start || modifiers.range_end;
  const isSingleSelected =
    modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle;
  // Outside days duplicate dates from the adjacent month — don't render them as
  // selection endpoints or the dual-month view looks like 3+ dates are selected.
  const isSelectedDay = (isSingleSelected || isRangeEndpoint) && !modifiers.outside;

  return (
    <button
      ref={ref}
      type="button"
      data-range-start={modifiers.range_start || undefined}
      data-range-end={modifiers.range_end || undefined}
      data-range-middle={modifiers.range_middle || undefined}
      data-selected-single={isSingleSelected || undefined}
      className={cn(
        'inline-flex h-9 w-9 items-center justify-center rounded-md p-0 text-sm font-normal transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-brand/30',
        modifiers.range_middle &&
          !modifiers.outside &&
          !modifiers.disabled &&
          'rounded-none bg-transparent text-foreground hover:bg-brand-subtle/80',
        !modifiers.disabled &&
          !modifiers.selected &&
          !modifiers.range_middle &&
          'hover:bg-surface-muted',
        modifiers.disabled && 'cursor-not-allowed',
        modifiers.today && !modifiers.selected && 'bg-surface-muted text-foreground',
        modifiers.outside && 'text-muted-foreground opacity-50',
        className,
        // Selected styles last so they win over outside/muted and DayPicker classes.
        isSelectedDay && 'bg-brand text-white opacity-100 hover:bg-brand hover:text-white'
      )}
      {...props}
    />
  );
}

export function Calendar({ className, classNames, showOutsideDays = true, components, ...props }: CalendarProps) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn(
          'relative flex flex-col gap-4 sm:flex-row sm:gap-6',
          defaultClassNames.months,
          classNames?.months
        ),
        month: cn('space-y-4', defaultClassNames.month, classNames?.month),
        month_caption: cn(
          'relative flex h-9 items-center justify-center px-8',
          defaultClassNames.month_caption,
          classNames?.month_caption
        ),
        caption_label: cn('text-sm font-medium', defaultClassNames.caption_label, classNames?.caption_label),
        nav: cn(
          'absolute inset-x-0 top-0 z-10 flex w-full items-center justify-between',
          defaultClassNames.nav,
          classNames?.nav
        ),
        button_previous: cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md p-0 opacity-50 hover:bg-surface-muted hover:opacity-100',
          defaultClassNames.button_previous,
          classNames?.button_previous
        ),
        button_next: cn(
          'inline-flex h-7 w-7 items-center justify-center rounded-md p-0 opacity-50 hover:bg-surface-muted hover:opacity-100',
          defaultClassNames.button_next,
          classNames?.button_next
        ),
        month_grid: cn('w-full border-collapse', defaultClassNames.month_grid, classNames?.month_grid),
        weekdays: cn('flex', defaultClassNames.weekdays, classNames?.weekdays),
        weekday: cn(
          'w-9 text-center text-[0.8rem] font-normal text-muted-foreground',
          defaultClassNames.weekday,
          classNames?.weekday
        ),
        week: cn('mt-2 flex w-full', defaultClassNames.week, classNames?.week),
        day: cn('group/day relative h-9 w-9 p-0 text-center text-sm', defaultClassNames.day, classNames?.day),
        day_button: cn(defaultClassNames.day_button, classNames?.day_button),
        range_start: cn('day-range-start rounded-l-md bg-brand-subtle', classNames?.range_start),
        range_middle: cn('day-range-middle rounded-none bg-brand-subtle', classNames?.range_middle),
        range_end: cn('day-range-end rounded-r-md bg-brand-subtle', classNames?.range_end),
        selected: cn(classNames?.selected),
        today: cn(classNames?.today),
        outside: cn(
          'day-outside text-muted-foreground opacity-50 [&.day-range-start]:bg-transparent [&.day-range-end]:bg-transparent [&.day-range-middle]:bg-transparent',
          defaultClassNames.outside,
          classNames?.outside
        ),
        disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled, classNames?.disabled),
        hidden: cn('invisible', defaultClassNames.hidden, classNames?.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === 'left' ? ChevronLeft : ChevronRight;
          return <Icon className="h-4 w-4" />;
        },
        DayButton: CalendarDayButton,
        ...components,
      }}
      {...props}
    />
  );
}
